/**
 * Public Onboarding API client.
 *
 * The invitee has no account — the raw token in the URL path is the sole
 * credential. We send it as a path param (NOT a query string) per the
 * backend mount: /api/onboarding/:token/...
 *
 * Backend: routes/public-onboarding.js (token-gated by requireInviteToken).
 */

import axios from 'axios';

const BASE = `${(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')}/onboarding`;

function url(token, subpath = '') {
  return `${BASE}/${encodeURIComponent(token)}${subpath}`;
}

export async function loadInvite(token) {
  const res = await axios.get(url(token));
  return res.data;
}

export async function saveDraft(token, payload) {
  const res = await axios.post(url(token, '/draft'), { payload });
  return res.data;
}

export async function submitOnboarding(token, payload) {
  const res = await axios.post(url(token, '/submit'), { payload });
  return res.data;
}

// ── Shareable links (separate endpoint set, separate token namespace) ──

const SHARED_BASE = `${(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')}/onboarding-links`;

function sharedUrl(token, subpath = '') {
  return `${SHARED_BASE}/${encodeURIComponent(token)}${subpath}`;
}

export async function loadSharedLink(token) {
  const res = await axios.get(sharedUrl(token));
  return res.data;
}

export async function submitSharedOnboarding(token, payload) {
  const res = await axios.post(sharedUrl(token, '/submit'), { payload });
  return res.data;
}

export async function uploadDocument(token, file, docType) {
  const form = new FormData();
  form.append('file', file);
  form.append('docType', docType);
  const res = await axios.post(url(token, '/documents'), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function deleteDocument(token, documentId) {
  const res = await axios.delete(url(token, `/documents/${encodeURIComponent(documentId)}`));
  return res.data;
}

export function documentStreamUrl(token, documentId) {
  return url(token, `/documents/${encodeURIComponent(documentId)}`);
}

export async function fetchDocumentBlob(token, documentId) {
  const res = await axios.get(url(token, `/documents/${encodeURIComponent(documentId)}`), {
    responseType: 'blob',
  });
  return {
    blob: res.data,
    mimeType: res.headers?.['content-type'] || res.data?.type || 'application/octet-stream',
  };
}
